import { Link } from 'react-router-dom'
import { Card } from '../ui/Card'
import { SectionLabel } from '../ui/SectionLabel'
import { Avatar } from '../ui/Avatar'
import { EmptyState } from '../ui/EmptyState'
import { Button } from '../ui/Button'
import type { ActivityEventDto, CotCircleType } from '../../types/community'

const DOT: Record<CotCircleType, string> = { Gaming: '#7fa66b', Family: '#6f8fbf', Friends: '#b9704f' }

function initialsOf(who: string): string {
  const t = (who || '').trim()
  return t ? t[0].toUpperCase() : '·'
}

interface ActivityCardProps {
  activities: ActivityEventDto[]
  pendingCount: number
  circleCount: number | null
  loading?: boolean
  error?: boolean
  onRetry?: () => void
}

/** Recent-activity card (COTC_FriendsApprovals R4 / DESIGN §3.1). */
export function ActivityCard({ activities, pendingCount, circleCount, loading, error, onRetry }: ActivityCardProps) {
  return (
    <Card padding="none" className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-5">
        <SectionLabel>Recent activity</SectionLabel>
        <span className="font-mono text-[10px] uppercase tracking-label text-ink-faint">
          {circleCount === null ? 'Your circles' : circleCount > 0 ? `Across your ${circleCount} circle${circleCount === 1 ? '' : 's'}` : 'No circles yet'}
        </span>
      </div>

      {pendingCount > 0 && (
        <Link
          to="/approvals"
          className="mx-5 mt-3 flex items-center justify-between rounded-btn bg-honey-bg px-4 py-3 text-[13px] text-honey-fg hover:brightness-[0.98]"
        >
          <span className="font-medium">
            {pendingCount} request{pendingCount === 1 ? '' : 's'} need{pendingCount === 1 ? 's' : ''} your yes
          </span>
          <span aria-hidden>→</span>
        </Link>
      )}

      <div className="mt-3 px-5 pb-3">
        {loading ? (
          <p className="py-4 font-mono text-[11px] uppercase tracking-label text-ink-faint">Loading…</p>
        ) : error ? (
          <div role="alert" className="py-3 text-[13px] text-ink-muted">
            <p>Recent activity could not be loaded.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>Retry activity</Button>
          </div>
        ) : activities.length === 0 ? (
          <EmptyState description={circleCount === 0 ? 'Nothing has happened yet. Once you join a circle, families joining, vouching and leaving all show up here.' : 'No recent activity. Circle updates will appear here when there is something to share.'} />
        ) : (
          <ul className="divide-y divide-line-row">
            {activities.map((a) => (
              <li key={a.id} className="flex items-start gap-3 py-3">
                <Avatar initials={initialsOf(a.who)} tintIndex={initialsOf(a.who).charCodeAt(0)} size={30} ringClass="border-paper-surface" />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] leading-snug text-ink">
                    <span className="font-semibold">{a.who || 'Someone'}</span> {a.what}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    {a.circleLabel && (
                      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-label text-ink-faint">
                        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: a.circleType ? DOT[a.circleType] : '#b3a48e' }} />
                        {a.circleLabel}
                      </span>
                    )}
                    <span className="font-mono text-[10px] uppercase tracking-label text-ink-fainter">{a.whenText}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-line-row bg-paper-row px-5 py-3 text-center">
        <Link to="/browse" className="text-[13px] font-medium text-terra-deep hover:text-terra-hover">
          {circleCount !== null && circleCount > 0 ? 'Find another group to join' : 'Find a group to join'}
        </Link>
      </div>
    </Card>
  )
}
